import type { CSSProperties, ReactNode } from 'react';

export type RedactedBlockProps = {
  /** When true, the ink slab fades + lifts to reveal the children behind. */
  reveal: boolean;
  /** Slab width — number sets px, string passes through (e.g. '100%'). Default '100%'. */
  w?: number | string;
  /** Reserved height under the slab — sized to the longest revealed token. */
  h: number;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
};

/**
 * Black-bar redaction used in the pass-and-play reveal screen. Children
 * (the secret role / word-keyword) sit below the slab; on `reveal` the
 * slab fades + translates up to expose them. Keep two cells for RÔLE
 * and MOT-CLÉ; tap-and-hold drives the `reveal` flag at the call site.
 */
export const RedactedBlock = ({
  reveal,
  w = '100%',
  h,
  className,
  style,
  children,
}: RedactedBlockProps) => {
  return (
    <div
      className={['relative', className ?? ''].filter(Boolean).join(' ')}
      style={{
        position: 'relative',
        display: 'block',
        width: w,
        minHeight: h,
        ...(style ?? {}),
      }}
    >
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
        {children}
      </div>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: -4,
          right: -4,
          top: -2,
          bottom: -2,
          background: 'var(--color-ink)',
          boxShadow: '1px 2px 0 rgba(0,0,0,.25)',
          transform: reveal ? 'translateY(-8px) rotate(-.5deg)' : 'translateY(0) rotate(0)',
          opacity: reveal ? 0 : 1,
          transition: 'opacity 220ms ease, transform 320ms cubic-bezier(.4,0,.2,1)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
};
